// Security Confirmation Dialogs for Payroll Operations

import React, { useState } from 'react';
import { format } from 'date-fns';
import { 
  AlertTriangle, 
  Lock, 
  DollarSign, 
  CheckCircle, 
  CreditCard,
  FileText,
  Eye,
  EyeOff,
  Shield
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';

import { PayrollAction, PayrollPeriod, MonthStatus } from '@/types/payroll-periods';
import { formatEUR } from '@/lib/payroll';

// Types for different confirmation scenarios
interface BaseConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
}


interface PayrollFinalizationConfirmationProps extends BaseConfirmationProps {
  period: PayrollPeriod;
  monthStatus: MonthStatus;
  totalGrossPay: number;
  totalNetPay: number;
  employeeCount: number;
  countryCode?: string;
}

interface PaymentProcessingConfirmationProps extends BaseConfirmationProps {
  period: PayrollPeriod;
  payments: Array<{
    employeeName: string;
    amount: number;
    accountInfo: string;
  }>;
  totalAmount: number;
  countryCode?: string;
}

interface PasswordConfirmationProps extends BaseConfirmationProps {
  title: string;
  description: string;
  requirePassword?: boolean;
  requireManagerApproval?: boolean;
  criticalAction?: boolean;
}

// Generic Security Confirmation Dialog
export const SecurityConfirmationDialog: React.FC<PasswordConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  requirePassword = false,
  requireManagerApproval = false,
  criticalAction = false,
  isLoading = false
}) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [acknowledgeRisk, setAcknowledgeRisk] = useState(false);

  const handleConfirm = () => {
    if (requirePassword && !password) return;
    if (criticalAction && !acknowledgeRisk) return;
    if (criticalAction && confirmationText !== 'CONFIRM') return;
    
    onConfirm();
  };

  const handleClose = () => {
    setPassword('');
    setConfirmationText('');
    setAcknowledgeRisk(false);
    onClose();
  };

  const isValid = (!requirePassword || password.length > 0) && 
                  (!criticalAction || (acknowledgeRisk && confirmationText === 'CONFIRM'));

  return (
    <AlertDialog open={isOpen} onOpenChange={handleClose}>
      <AlertDialogContent className="sm:max-w-[500px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {criticalAction ? (
              <AlertTriangle className="h-5 w-5 text-red-500" />
            ) : (
              <Shield className="h-5 w-5 text-blue-500" />
            )}
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-left">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          {criticalAction && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="acknowledge-risk"
                  checked={acknowledgeRisk}
                  onCheckedChange={(checked) => setAcknowledgeRisk(checked as boolean)}
                />
                <Label htmlFor="acknowledge-risk" className="text-sm font-medium text-red-800">
                  I understand this action cannot be undone
                </Label>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmation-text" className="text-sm font-medium text-red-800">
                  Type "CONFIRM" to proceed:
                </Label>
                <Input
                  id="confirmation-text"
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder="Type CONFIRM"
                  className="border-red-300 focus:border-red-500"
                />
              </div>
            </div>
          )}

          {requirePassword && (
            <div className="space-y-2">
              <Label htmlFor="password">Enter your password to confirm:</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </Button>
              </div>
            </div>
          )}

          {requireManagerApproval && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                This action requires manager approval. You will be notified when approved.
              </p>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleClose}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
            className={criticalAction ? "bg-red-600 hover:bg-red-700" : undefined}
          >
            {isLoading ? "Processing..." : "Confirm"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};


// Payroll Finalization Confirmation Dialog
export const PayrollFinalizationConfirmation: React.FC<PayrollFinalizationConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  period,
  monthStatus,
  totalGrossPay,
  totalNetPay,
  employeeCount,
  countryCode,
  isLoading = false
}) => {
  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [acknowledgeFinalization, setAcknowledgeFinalization] = useState(false);

  const formatCurrency = (amount: number) => {
    return countryCode ? formatEUR(amount) : `$${amount.toFixed(2)}`;
  };

  const handleConfirm = () => {
    if (!password || confirmationText !== 'FINALIZE' || !acknowledgeFinalization) return;
    onConfirm();
  };

  const handleClose = () => {
    setPassword('');
    setConfirmationText('');
    setAcknowledgeFinalization(false);
    onClose();
  };

  const isValid = password.length > 0 && confirmationText === 'FINALIZE' && acknowledgeFinalization;

  return (
    <AlertDialog open={isOpen} onOpenChange={handleClose}>
      <AlertDialogContent className="sm:max-w-[600px]">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-red-500" />
            Finalize Payroll - {period.displayName}
          </AlertDialogTitle>
          <AlertDialogDescription>
            This action will finalize the payroll and cannot be undone. Please review carefully.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          {/* Payroll Summary */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-3">
            <h4 className="font-medium">Payroll Summary</h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-muted-foreground">Employees</p>
                <p className="text-xl font-bold">{employeeCount}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Gross Pay</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(totalGrossPay)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net Pay</p>
                <p className="text-xl font-bold text-blue-600">{formatCurrency(totalNetPay)}</p>
              </div>
            </div>
          </div>

          {/* Critical Action Warning */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="acknowledge-finalization"
                checked={acknowledgeFinalization}
                onCheckedChange={(checked) => setAcknowledgeFinalization(checked as boolean)}
              />
              <Label htmlFor="acknowledge-finalization" className="text-sm font-medium text-red-800">
                I understand that finalizing payroll cannot be undone
              </Label>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmation-text" className="text-sm font-medium text-red-800">
                Type "FINALIZE" to proceed:
              </Label>
              <Input
                id="confirmation-text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Type FINALIZE"
                className="border-red-300 focus:border-red-500"
              />
            </div>
          </div>

          {/* Password Confirmation */}
          <div className="space-y-2">
            <Label htmlFor="password">Enter your password to confirm finalization:</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleClose}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
            className="bg-red-600 hover:bg-red-700"
          >
            {isLoading ? "Finalizing..." : "Finalize Payroll"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

// Payment Processing Confirmation Dialog
export const PaymentProcessingConfirmation: React.FC<PaymentProcessingConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  period,
  payments,
  totalAmount,
  countryCode,
  isLoading = false
}) => {
  const [password, setPassword] = useState('');
  const [confirmationText, setConfirmationText] = useState('');
  const [acknowledgePayment, setAcknowledgePayment] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);

  const formatCurrency = (amount: number) => {
    return countryCode ? formatEUR(amount) : `$${amount.toFixed(2)}`;
  };

  const handleConfirm = () => {
    if (!password || confirmationText !== 'PROCESS PAYMENTS' || !acknowledgePayment) return;
    onConfirm();
  };

  const handleClose = () => {
    setPassword('');
    setConfirmationText('');
    setAcknowledgePayment(false);
    onClose();
  };

  const isValid = password.length > 0 && confirmationText === 'PROCESS PAYMENTS' && acknowledgePayment;

  return (
    <AlertDialog open={isOpen} onOpenChange={handleClose}>
      <AlertDialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-green-500" />
            Process Payments - {period.displayName}
          </AlertDialogTitle>
          <AlertDialogDescription>
            This will initiate bank transfers to {payments.length} employees. 
            Please review all payment details carefully.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-4">
          {/* Payment Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium">Payment Summary</h4>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowPaymentDetails(!showPaymentDetails)}
              >
                {showPaymentDetails ? 'Hide' : 'Show'} Details
              </Button>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Total Payments</p>
                <p className="text-xl font-bold">{payments.length}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(totalAmount)}</p>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          {showPaymentDetails && (
            <div className="max-h-40 overflow-y-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 border-b">Employee</th>
                    <th className="text-right p-2 border-b">Amount</th>
                    <th className="text-left p-2 border-b">Account</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-2">{payment.employeeName}</td>
                      <td className="p-2 text-right font-medium">{formatCurrency(payment.amount)}</td>
                      <td className="p-2 text-muted-foreground">{payment.accountInfo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Critical Action Warning */}
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="acknowledge-payment"
                checked={acknowledgePayment}
                onCheckedChange={(checked) => setAcknowledgePayment(checked as boolean)}
              />
              <Label htmlFor="acknowledge-payment" className="text-sm font-medium text-red-800">
                I confirm all payment details are correct and authorize these transfers
              </Label>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmation-text" className="text-sm font-medium text-red-800">
                Type "PROCESS PAYMENTS" to proceed:
              </Label>
              <Input
                id="confirmation-text"
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder="Type PROCESS PAYMENTS"
                className="border-red-300 focus:border-red-500"
              />
            </div>
          </div>

          {/* Password Confirmation */}
          <div className="space-y-2">
            <Label htmlFor="password">Enter your password to authorize payments:</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
            />
          </div>
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleClose}>Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
            className="bg-green-600 hover:bg-green-700"
          >
            {isLoading ? "Processing..." : "Process Payments"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};